/// <amd-module name="Dashboards/DashboardView/src/redux/sagas/ThrottlingSaga" />
import { IDashboardRateLimitMonitorService } from "Dashboards/RateLimiter/DashboardRateLimitMonitorService";
import { DashboardThrottlingInfo, IDashboardThrottlingService } from "Dashboards/RateLimiter/DashboardThrottlingService";
import { IThrottleOnHighResourceUsage } from "Dashboards/RateLimiter/IThrottleHighResourceUsage";
import { effects, END, eventChannel, SagaIterator } from "redux-saga";
import { IVssPageContext } from "VSS/Platform/Context";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { DashboardActions, DashboardActionTypes } from "../actions/Actions";
import * as Selectors from "../selectors/Selectors";

export namespace ThrottlingSagas {
    export function* rootSaga(): SagaIterator {
        // Throttling needs to be started once per page, so waiting for first DashboardInitialized action
        // and then starting thr throttling
        yield effects.take(DashboardActionTypes.NotifyDashboardInitialized);
        yield effects.call(enableThrottling);
    }

    export function* enableThrottling(): SagaIterator {
        const refreshTimeMinutes = (yield effects.select(Selectors.getRefreshInterval)) as any;

        if (refreshTimeMinutes > 0) {
            const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
            const isMonitoringEnabled: boolean = (yield effects.call(
                isFeatureFlagEnabled,
                pageContext,
                "WebAccess.Dashboards.MonitorResourceUtilizationHeaders"
            )) as any;
            const allowThrottling: boolean = (yield effects.call(
                isFeatureFlagEnabled,
                pageContext,
                "WebAccess.Dashboards.SuppressAutoRefreshOnHighResourceUtilization"
            )) as any;

            if (isMonitoringEnabled) {
                const throttlingService: IDashboardThrottlingService = (yield effects.call(
                    [pageContext, pageContext.getService],
                    "IDashboardThrottlingService"
                )) as any;
                const rateLimitMonitorService: IDashboardRateLimitMonitorService = (yield effects.call(
                    [pageContext, pageContext.getService],
                    "IDashboardRateLimitMonitorService"
                )) as any;

                // The callback protocol between IDashboardRateLimitMonitorService and IDashboardThrottlingService
                const throttleSignalHandler: IThrottleOnHighResourceUsage = {
                    flagAsThrottled: info => throttlingService.flagAsThrottled(info)
                };

                // If monitoring is enabled, start rate limit monitor and give it handler to throttling service
                // This call connects IDashboardRateLimitMonitorService and IDashboardThrottlingService,
                // so that they can communicate
                // Note that if allowThrottling is false, this is the only code path that executes - we look for isThrottled in the RefreshSaga directly to see the state for telemetry
                // This will be removed once the FFs are on, and this data (isAtHighUsage) will be promoted to the state properly
                yield effects.call([rateLimitMonitorService, rateLimitMonitorService.startListening], throttleSignalHandler);

                try {
                    // If throttling flag is set, actually listen to callbacks and show banner
                    if (allowThrottling) {
                        yield effects.put(DashboardActions.setAllowThrottling(true));

                        // Create channel to get callbacks from service
                        const channel = (yield effects.call(createCallbackChannel, throttlingService)) as any;

                        // Wait for channel to send data and create action based on that
                        try {
                            while (true) {
                                const throttled = (yield effects.take(channel)) as any;
                                yield effects.put(DashboardActions.setThrottlingWarning(throttled));
                            }
                        } finally {
                            // Runs when task is cancelled by the tear down (e.g. FPS navigation)
                            if (yield effects.cancelled()) {
                                // Close the channel so that callback is removed
                                channel.close();
                            }
                        }
                    } else {
                        // If we are not throttling, we still need to stop rate limit service
                        // so wait for global redux tear-down to stop it
                        yield effects.take(END.type);
                    }
                } finally {
                    // Stop rate limit service from listening
                    yield effects.call([rateLimitMonitorService, rateLimitMonitorService.stopListening]);
                }
            }
        }
    }

    function createCallbackChannel(throttlingService: IDashboardThrottlingService) {
        return eventChannel<boolean>(emitter => {
            // Callback that DashboardThrottlingService executes when it detects throttling
            // Note that it has internal timer too, and only calls this call back once every 8 minutes
            const showThrottlingHandler = (info: DashboardThrottlingInfo) => {
                emitter(info.isThrottled);
            };
            throttlingService.subscribe(showThrottlingHandler);

            // This is "unsubscribe" callback of channels which gets called when task terminates
            return () => {
                // Remove the callback we set up
                throttlingService.unsubscribe(showThrottlingHandler);
            };
        });
    }
}
